// OPTIONS HEDGING · Execution Eligibility of one Scanner pair, in full engineering detail. The MAIN
// backend re-validates the pair from its OWN live data - the Scanner's numbers are never used for
// the verdict - and the sizing PREVIEW shows the eleven capital lines from the broker's read-only
// margin calculators and planned pairs. Traders use the compact TradeTicket, which runs the same
// calls automatically and shows these tables under "View execution details".
import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { age, px, qty } from "@/components/options/OptionsCommon";

const rs = (v) => (v === null || v === undefined ? "—" : `₹${px(v)}`);

function Line({ n, label, value, source, testId }) {
  return (
    <tr className="mono" data-testid={testId}>
      <td className="px-1.5 text-[var(--term-muted)]">{n}</td>
      <td className="px-1.5">{label}</td>
      <td className="px-1.5 text-right font-bold">{value}</td>
      <td className="px-1.5 text-[10px] text-[var(--term-muted)]">{source}</td>
    </tr>
  );
}

function legLine(l) {
  if (!l || l.status !== "OK") return "not available";
  return `premium ${rs(l.broker_premium)} · total ${rs(l.broker_total)} · charges ${rs(l.charges)}`;
}

export function HedgePreview({ p, startable, reasons, onCreate, creating, created }) {
  if (!p) return null;
  const L = p.lines;
  const b = L["6_broker_basket_capital"];
  const lot = p.cash_model;
  return (
    <div className="space-y-1" data-testid="hedge-preview">
      <div className="mono">
        1 lot = {qty(lot.broker_lot_quantity)} qty × {lot.units_per_quantity} unit(s) = ₹{px(lot.lot_cash_multiplier)} per 1.00 ·
        opportunity per pair <b>{rs(p.opportunity_per_pair)}</b>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          <Line n={1} label="CE requested premium" value={rs(L["1_ce_requested_premium"])} source="CE offer × lot qty × units" />
          <Line n={2} label="PE requested premium" value={rs(L["2_pe_requested_premium"])} source="PE offer × lot qty × units" />
          <Line n={3} label="CE broker result" value={legLine(L["3_ce_broker"])} source="margin calculator, CE alone" />
          <Line n={4} label="PE broker result" value={legLine(L["4_pe_broker"])} source="margin calculator, PE alone" />
          <Line n={5} label="Combined requested premium" value={rs(L["5_combined_requested_premium"])} source="1 + 2" />
          <Line n={6} label="Broker basket capital" testId="hedge-line-6"
            value={<>{rs(b.final_total)}{b.below_limit && <span className="ml-1 text-[#B45309]">BELOW LIMIT</span>}</>}
            source="basket calculator, BUY CE + BUY PE, NRML, LIMIT at offers" />
          <Line n={7} label="Charges" value={`entry ${rs(L["7_charges"].entry)} · exit est. ${rs(L["7_charges"].exit_est)}`}
            source="3 + 4 charges; exit = calculator, SELL both at bids" />
          <Line n={8} label="Capital per pair (sizing)" testId="hedge-line-8"
            value={rs(L["8_capital_per_pair"].value)}
            source={`max(broker basket, requested premium) + charges · basis ${L["8_capital_per_pair"].basis || "—"}`} />
          <Line n={9} label="Allocated fund" value={rs(L["9_allocated_fund"])} source="you" />
          <Line n={10} label="Reserve / committed"
            value={`${rs(L["10_reserved_committed"].reserve)} (${L["10_reserved_committed"].reserve_pct}%) / ${rs(L["10_reserved_committed"].committed)}`}
            source="reserve % of fund; nothing held yet" />
          <Line n={11} label="Remaining" value={rs(L["11_remaining"])} source="9 − committed − in flight − reserve" />
        </tbody>
      </table>
      <div className="mono" data-testid="hedge-planned">
        Planned pairs <b>{p.planned_pairs ?? "—"}</b> (max lots {p.max_lots})
        {p.pool && <> · reserved {rs(p.pool.reserved)} · {p.pool.limited ? <>available within broker funds {rs(p.pool.headroom)}</> : "SHADOW: not limited"}</>}
        {p.broker_funds && <> · broker available {rs(p.broker_funds.usable)} ({p.broker_funds.basis}, ×{p.broker_funds.margin_multiple} buffer)</>}
      </div>
      <div className={`font-bold ${startable ? "text-[#00C805]" : "text-[#FF3B30]"}`} data-testid="hedge-startable">
        {startable ? "Sizable and eligible" : `Not startable: ${reasons.join(", ")}`}
      </div>
      {onCreate && p.sizable && (
        <div className="flex items-center gap-2">
          <button onClick={onCreate} disabled={creating || !!created} className="px-2 py-1 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="hedge-create">
            {created ? "Created" : creating ? "Creating…" : "Create trade"}</button>
          <span className="text-[10px] text-[var(--term-muted)]">{created ? <a className="underline" href="/options/hedge">Open Options Trading →</a>
            : "Nothing is sent yet: you start the trade on Options Trading, which re-checks everything."}</span>
        </div>
      )}
      {p.broker_errors && Object.keys(p.broker_errors).length > 0 && (
        <div className="text-[#B45309]">Broker calculator: {Object.entries(p.broker_errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
      )}
    </div>
  );
}

const input = "bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1 w-24";

export function LegsTable({ ce, pe }) {
  return (
    <table className="w-full border-collapse" data-testid="hedge-legs-table">
      <thead className="bg-[var(--term-s3)]"><tr>{["Leg", "Bid × qty", "Offer × qty", "Spread", "OI", "Depth age", "LTP (trade age)"].map((h) => <th key={h} className="px-1.5 py-1 text-right first:text-left">{h}</th>)}</tr></thead>
      <tbody><Leg label="CE" l={ce} /><Leg label="PE" l={pe} /></tbody>
    </table>
  );
}

function Leg({ label, l }) {
  if (!l) return null;
  return (
    <tr className="mono">
      <td className="px-1.5 text-left font-bold">{label} {l.tradingsymbol}</td>
      <td className="px-1.5 text-right">{px(l.bid)} × {qty(l.bid_qty)}</td>
      <td className="px-1.5 text-right font-bold">{px(l.ask)} × {qty(l.ask_qty)}</td>
      <td className="px-1.5 text-right">{l.spread_pct != null ? `${l.spread_pct}%` : "—"}</td>
      <td className="px-1.5 text-right">{qty(l.oi)}</td>
      <td className="px-1.5 text-right">{age(l.depth_age_s)}</td>
      <td className="px-1.5 text-right text-[var(--term-muted)]">{px(l.ltp)} ({age(l.trade_age_s)})</td>
    </tr>
  );
}

export default function HedgeEligibility({ ceToken, peToken, pairId }) {
  const [env, setEnv] = useState("SHADOW");
  const [minOpp, setMinOpp] = useState("");
  const [minOi, setMinOi] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [fund, setFund] = useState("");
  const [maxLots, setMaxLots] = useState("");
  const [target, setTarget] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [pre, setPre] = useState(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const overrides = () => {
    const o = {};
    if (minOpp !== "") o.min_opportunity = Number(minOpp);
    if (minOi !== "") o.min_oi_execution = Number(minOi);
    if (maxLots !== "") o.max_lots = Number(maxLots);
    if (target !== "") o.profit_target = Number(target);
    if (stopLoss !== "") o.stop_loss = Number(stopLoss);
    return Object.keys(o).length ? o : null;
  };
  const run = async () => {
    setBusy(true); setErr(null); setPre(null);
    try {
      const r = await api.post("/options-hedge/candidates/evaluate",
        { environment: env, ce_token: ceToken, pe_token: peToken, overrides: overrides() });
      setRes(r.data);
    } catch (e) { setErr(apiError(e)); setRes(null); } finally { setBusy(false); }
  };
  const runPreview = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/options-hedge/trades/preview",
        { environment: env, ce_token: ceToken, pe_token: peToken, allocated_fund: Number(fund), overrides: overrides() });
      setRes(r.data.eligibility); setPre(r.data); setCreated(null);
    } catch (e) { setErr(apiError(e)); setPre(null); } finally { setBusy(false); }
  };
  const create = async () => {
    setCreating(true); setErr(null);
    try {
      const r = await api.post("/options-hedge/trades",
        { environment: env, ce_token: ceToken, pe_token: peToken, allocated_fund: Number(fund), overrides: overrides(), pair_id: pairId });
      setCreated(r.data.trade);
    } catch (e) { setErr(apiError(e)); } finally { setCreating(false); }
  };
  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-2" data-testid="options-hedge-eligibility">
      <div className="flex items-center gap-2 flex-wrap">
        <b>Options Hedge · Execution eligibility</b>
        <select className={input} value={env} onChange={(e) => setEnv(e.target.value)} data-testid="hedge-env">
          <option value="SHADOW">SHADOW</option><option value="LIVE">LIVE</option>
        </select>
        <input className={input} placeholder="Min opp" value={minOpp} onChange={(e) => setMinOpp(e.target.value)} title="Spread to Maintain: Offer Opportunity per price unit must be at least this (default from Options Hedging settings)" />
        <input className={input} placeholder="Min OI" value={minOi} onChange={(e) => setMinOi(e.target.value)} title="Minimum OI on both legs (default from Options Hedging settings)" />
        <button onClick={run} disabled={busy} className="px-2 py-1 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="hedge-evaluate">{busy ? "Working…" : "Evaluate"}</button>
        <input className={input} placeholder="Allocated ₹" value={fund} onChange={(e) => setFund(e.target.value)} data-testid="hedge-fund" title="Allocated fund for this trade" />
        <input className={input} placeholder="Max lots" value={maxLots} onChange={(e) => setMaxLots(e.target.value)} title="Maximum hedge pairs (default from Options Hedging settings)" />
        <input className={input} placeholder="Target ₹" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="hedge-target" title="Profit target: square off when net unrealized P&L at the BID is strictly above this (default from Options Hedging settings)" />
        <input className={input} placeholder="Stop loss ₹" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} data-testid="hedge-stop-loss" title="Optional stop loss: square off when net unrealized P&L at the BID is at or below minus this" />
        <button onClick={runPreview} disabled={busy || !(Number(fund) > 0)} className="px-2 py-1 rounded-sm font-bold border border-[#2563EB] text-[#2563EB]" data-testid="hedge-preview-btn">Preview sizing</button>
      </div>
      <div className="text-[10px] text-[var(--term-muted)]">Priced on the live OFFER book from the backend's own feed. An old LTP or no volume is only a warning. Sizing uses the broker's read-only margin calculators. Nothing is created, reserved or placed.</div>
      {err && <div className="text-[#FF3B30]">{err}</div>}
      {res && (
        <>
          <div className={`font-bold ${res.eligible ? "text-[#00C805]" : "text-[#FF3B30]"}`} data-testid="hedge-verdict">
            {res.eligible ? "EXECUTION-ELIGIBLE" : "NOT EXECUTION-ELIGIBLE"} · {res.environment}
          </div>
          <div className="mono">
            {res.underlying} {res.expiry} (DTE {res.dte}) · spot {px(res.spot)} ({age(res.spot_age_s)}) · strike diff {px(res.strike_diff)} ·
            offers {px(res.combined_offer)} → opportunity <b>{px(res.opportunity_offer)}</b> (min {px(res.min_opportunity)})
          </div>
          <table className="w-full border-collapse">
            <thead className="bg-[var(--term-s3)]"><tr>{["Leg", "Bid × qty", "Offer × qty", "Spread", "OI", "Depth age", "LTP (trade age)"].map((h) => <th key={h} className="px-1.5 py-1 text-right first:text-left">{h}</th>)}</tr></thead>
            <tbody><Leg label="CE" l={res.ce} /><Leg label="PE" l={res.pe} /></tbody>
          </table>
          {res.reasons.length > 0 && <div className="text-[#FF3B30]" data-testid="hedge-reasons">Blocked by: {res.reasons.join(", ")}</div>}
          {res.warnings.length > 0 && <div className="text-[#B45309]" data-testid="hedge-warnings">Warnings (not blocking): {res.warnings.join(", ")}</div>}
          {pre && pre.preview && <HedgePreview p={pre.preview} startable={pre.startable} reasons={pre.reasons}
            onCreate={create} creating={creating} created={created} />}
          {pre && !pre.preview && <div className="text-[#FF3B30]" data-testid="hedge-not-sized">Not sized: {pre.reasons.join(", ")} {pre.capital_error || ""}</div>}
        </>
      )}
    </div>
  );
}
